import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ToastVariant = "success" | "error" | "info";

type ToastItem = { id: string; message: string; variant: ToastVariant };

type ToastContextValue = {
  showToast: (message: string, variant?: ToastVariant, durationMs?: number) => void;
  dismissToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_MS = 5200;
const TOAST_MS_ERROR_LONG = 14000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, variant: ToastVariant = "info", durationMs?: number) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      setToasts((t) => [...t, { id, message, variant }]);
      const ms =
        durationMs ??
        (variant === "error" && message.length > 120 ? TOAST_MS_ERROR_LONG : TOAST_MS);
      window.setTimeout(() => dismissToast(id), ms);
    },
    [dismissToast],
  );

  const toastValue = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={toastValue}>
      {children}
      <div className="toast-viewport" aria-live="polite" aria-relevant="additions text">
        {toasts.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`toast toast--${t.variant}`}
            onClick={() => dismissToast(t.id)}
          >
            <span className="toast__message">{t.message}</span>
            <span className="toast__dismiss" aria-hidden>
              ×
            </span>
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
